/**
 * SummaryStep Component
 * Step 3 of the deployment wizard - review and confirm deployment
 * Captures screenshot of summary before deployment
 */

import React from 'react';
import {
  Text,
  tokens,
} from '@fluentui/react-components';
import { SummaryContent, DialogFooter, useDeployWithScreenshot, WizardDialog } from '../shared';
import type { SummaryStepProps } from './types';

export const SummaryStep: React.FC<SummaryStepProps> = ({
  policy,
  summary,
  isRefreshing = false,
  refreshError = null,
  onRetryRefresh,
  onBack,
  onConfirm,
  onClose,
}) => {
  const { summaryRef, handleDeploy, deployError, primaryLabel, primaryDisabled } = useDeployWithScreenshot({
    screenshotName: policy.policyName,
    screenshotVariant: 'dashboard',
    onConfirm,
  });

  if (!summary) return null;

  // Transform tenant rule summaries to the format expected by SummaryContent
  const ruleSummaries = summary.tenantRuleSummaries.map(({ tenantId, tenantName, prefix, rules }) => ({
    id: tenantId,
    name: tenantName,
    prefix,
    rules,
  }));

  // Screenshot header content
  const screenshotHeader = (
    <div>
      <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: 4 }}>
        Multi-Tenant Deployment Summary
      </Text>
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
        Policy: {policy.policyName} | Date: {new Date().toLocaleString()}
      </Text>
    </div>
  );

  return (
    <WizardDialog
      title={`Multi-Tenant Deployment Summary \u2013 ${policy.policyName}`}
      onClose={onClose}
      maxWidth="850px"
      contentStyle={{ padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '65vh' }}
    >
            <SummaryContent
              ref={summaryRef}
              ruleSummaries={ruleSummaries}
              totalRulesCount={summary.totalRulesCount}
              sitsExisting={summary.sitsExisting}
              sitsNew={summary.sitsNew}
              labelsExisting={summary.labelsExisting}
              labelsNew={summary.labelsNew}
              isRefreshing={isRefreshing}
              refreshError={refreshError}
              onRetryRefresh={onRetryRefresh}
              targetTenantCount={ruleSummaries.length}
              introText="Below is a summary of the rules that will be deployed to the target tenants, including the Sensitive Information Types (SITs) and Labels that are already present and those that will be created."
              labelPrefixNote="Parent and standalone labels, as well as policies, will be prefixed with each tenant's configured prefix when first created; existing labels are never renamed, and child labels are not prefixed."
              screenshotHeader={screenshotHeader}
            />
            <DialogFooter
              showBack
              onBack={onBack}
              onCancel={onClose}
              onPrimary={handleDeploy}
              primaryLabel={primaryLabel}
              primaryDisabled={primaryDisabled || isRefreshing || !!refreshError}
            >
              {deployError && (
                <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: 13 }}>{deployError}</Text>
              )}
            </DialogFooter>
    </WizardDialog>
  );
};

export default SummaryStep;
